function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * ----------------------------------------------------------------------------
 * Event Handler
 * ----------------------------------------------------------------------------
 * The iamotaEventHandler class provides a mechanism to handle custom events.
 * It allows subscribing to, unsubscribing from, and triggering events, as well as applying filters to event data.
 * ----------------------------------------------------------------------------
 *
 * Usage example:
 *
 * import iamotaEventHandler from 'path/to/iamotaEventHandler';
 *
 * // Subscribe to an event
 * iamotaEventHandler.subscribe('myEvent', (data) => {
 *     console.log('Event triggered with data:', data);
 * });
 *
 * // Trigger the event
 * iamotaEventHandler.trigger('myEvent', { key: 'value' });
 *
 * // Apply filters to event data
 * const filteredData = await iamotaEventHandler.applyFilters('myEvent', valueToFilter, additionalData);
 *
 * // Unsubscribe from the event
 * iamotaEventHandler.unsubscribe('myEvent', myCallback);
 *
 * // Unsubscribe all callbacks from the event
 * iamotaEventHandler.unsubscribeAll('myEvent');
 */

// -------------------------------------
// Dependencies
// -------------------------------------

import Debug from 'scripts/libs/debug';
import colors from 'scripts/libs/colors';

// -------------------------------------
// Functionality
// -------------------------------------

class iamotaEventHandler {
  /**
   * Subscribe to a specific event with a callback function.
   *
   * @param {string} event - The event name to subscribe to.
   * @param {function} callback - The callback function to be called when the event is triggered.
   * @param {number} [priority=100] - The priority of the callback (lower number means higher priority).
   * @throws {Error} Throws an error if the callback is not a function.
   */
  static subscribe(event, callback) {
    var _options$context;
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    // Merge defaults with user-provided options
    options = _objectSpread({
      context: null,
      priority: 100
    }, options);
    if (!event.includes(':')) {
      console.warn("Event \"".concat(colors.green(event), "\" is not namespaced. Consider adding a namespace."));
    }
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    // Register the event listener
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event).push({
      callback,
      options
    });
    this.events.get(event).sort((a, b) => a.options.priority - b.options.priority);
    var callbackSource = options.source || callback.name || 'anonymous function';
    var contextLabel = (_options$context = options.context) !== null && _options$context !== void 0 && _options$context.nodeName ? "on a specific ".concat(colors.blue("<".concat(options.context.nodeName.toLowerCase(), ">")), " context") : 'globally';
    Debug.log("\uD83D\uDD14 ".concat(colors.gray(callbackSource), " subscribed to event: ").concat(colors.green(event), " ").concat(contextLabel, " (priority ").concat(colors.cyan(options.priority), ")"), 3, {
      callback,
      options,
      allListeners: this.events
    });

    // Replay atomic event if it has already fired
    if (this.atomicEvents.has(event)) {
      var eventData = this.atomicEvents.get(event);
      if (eventData._options.context && !document.body.contains(eventData._options.context)) {
        Debug.log("Skipping atomic event replay for ".concat(colors.green(event), " for new listener since the context it was bound to no longer exists"), 2, {
          eventData,
          callback
        });
        return; // Skip replaying if the context is no longer in the DOM
      }
      Debug.log("\u26A1 ".concat(colors.magenta('Replaying atomic event'), " that already fired: ").concat(colors.green(event), " for new listener"), 2, {
        eventData,
        callback
      });
      callback(eventData);
    }
  }

  /**
   * Unsubscribe a specific callback from an event.
   *
   * @param {string} event - The event name to unsubscribe from.
   * @param {function} callback - The callback function to be removed.
   */
  static unsubscribe(event, callback) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    options = _objectSpread({
      context: null
    }, options);
    if (this.events.has(event)) {
      var callbacks = this.events.get(event).filter(item => item.callback !== callback || options.context && item.context !== options.context);
      if (callbacks.length > 0) {
        this.events.set(event, callbacks);
      } else {
        this.events.delete(event);
      }
      Debug.log("\uD83D\uDD15 Unsubscribed from event: ".concat(colors.green(event), " ").concat(options.context ? "on context ".concat(colors.yellow(options.context)) : 'globally'), 2);
    }
  }

  /**
   * Unsubscribe all callbacks from an event.
   * WARNING: this doesn't remove any "local" listeners added directly to the DOM element (context)
   * @param {string} event - The event name to unsubscribe all callbacks from.
   */
  static unsubscribeAll(event) {
    if (this.events.has(event)) {
      var count = this.events.get(event).length;
      this.events.delete(event);

      // Debug logging
      Debug.log("\uD83D\uDD15 Unsubscribed all (".concat(colors.cyan(count), " listeners) from event: ").concat(colors.green(event)));
    }
  }

  /**
   * Trigger one or multiple events, calling all subscribed callbacks asynchronously.
   *
   * @param {string|array|null|undefined} event - The event name or array of event names to trigger.
   * @param {object} eventData - Parameters to pass to the callback functions.
   * @param {object} options - Event options (e.g., context, isAtomic).
   * @returns {Promise<void>} A promise that resolves after all callbacks have been executed.
   */
  static trigger(event) {
    var _arguments = arguments,
      _this = this;
    return _asyncToGenerator(function* () {
      var eventData = _arguments.length > 1 && _arguments[1] !== undefined ? _arguments[1] : {};
      var options = _arguments.length > 2 && _arguments[2] !== undefined ? _arguments[2] : {};
      if (!event) return; // Ignore null or undefined events

      // If the event is an array, trigger a separate event for each item in the array
      if (Array.isArray(event)) {
        eventData['_primaryEvent'] = event[0]; // Set the primary event for the first item in the array
        eventData['_events'] = event; // Store all events in the additional data
        for (var e of event) {
          yield _this.trigger(e, eventData, options);
        }
        return;
      }

      // Add the event key (i.e. the full event name) to the parameters
      // (e.g. in case you're watching an entire namespace, but still want to differentiate child events)
      eventData['_event'] = event;
      eventData['_events'] = eventData['_events'] || [event];
      eventData['_primaryEvent'] = eventData['_primaryEvent'] || event;
      eventData['_options'] = options;

      // If it's an "atomic event", store the payload for future subscribers
      if (options.isAtomic) {
        _this.atomicEvents.set(event, eventData);
      }

      // Get Callbacks watching for the specific event, or any partial namespace matches
      // e.g. 'namespace:subnamespace:event' would trigger for subscribers of 'namespace:subnamespace:event', 'namespace:subnamespace', and 'namespace'
      var matchingCallbacks = [];
      var eventParts = event.split(':');
      for (var i = eventParts.length; i > 0; i--) {
        var currentEvent = eventParts.slice(0, i).join(':');
        var eventCallbacks = _this.events.get(currentEvent);
        if (eventCallbacks) {
          matchingCallbacks.push(...eventCallbacks);
        }
      }

      // Reduce to only callbacks with the correct context (or global)
      var globalCallbacks = matchingCallbacks.filter(listener => !listener.options.context);
      var contextCallbacks = matchingCallbacks.filter(listener => options.context && listener.options.context === options.context);
      var callbacks = [...contextCallbacks, ...globalCallbacks];

      // Sort the callbacks by priorty
      callbacks.sort((a, b) => a.options.priority - b.options.priority);
      if (options.context && contextCallbacks.length) {
        // If the event is context-bound, notify matching context-specific listeners or global listeners
        Debug.log("\u26A1 ".concat(colors.magenta('Triggering event'), ": ").concat(colors.green(event), " on context ").concat(colors.yellow(options.context), " by notifying ").concat(colors.cyan(callbacks.length), " callbacks (").concat(colors.cyan(contextCallbacks.length), " context, ").concat(colors.cyan(globalCallbacks.length), " global)"), 2, {
          eventData,
          callbacks,
          globalCallbacks,
          contextCallbacks
        });
        yield Promise.all(callbacks.map(_ref => {
          var {
            callback
          } = _ref;
          return Promise.resolve(callback(eventData)).catch(error => {
            console.error("Error in callback for context-bound event ".concat(colors.green(event), "..."), error);
          });
        }));
      } else if (!callbacks.length) {
        // Log if nothing was listening
        var differentContextCallbacks = callbacks.filter(listener => options.context && listener.options.context !== options.context);
        if (differentContextCallbacks.length) {
          Debug.log("\u26A1 ".concat(colors.magenta('Triggering event'), ": ").concat(colors.green(event), " but nothing was listening on the given context (but ").concat(colors.cyan(differentContextCallbacks.length), " were listening on a different context)"), 4, {
            eventData,
            options,
            globalCallbacks,
            contextCallbacks,
            allListeners: _this.events
          });
        } else {
          Debug.log("\u26A1 ".concat(colors.magenta('Triggering event'), ": ").concat(colors.green(event), " but nothing was listening"), 4, {
            eventData,
            options,
            globalCallbacks,
            contextCallbacks,
            allListeners: _this.events
          });
        }
      } else {
        // If the event wasn't context-bound, notify all listeners
        Debug.log("\u26A1 ".concat(colors.magenta('Triggering event'), ": ").concat(colors.green(event), " by notifying ").concat(colors.cyan(callbacks.length), " callbacks"), 2, {
          eventData,
          callbacks,
          globalCallbacks,
          contextCallbacks
        });
        yield Promise.all(callbacks.map(_ref2 => {
          var {
            callback
          } = _ref2;
          return Promise.resolve(callback(eventData)).catch(error => {
            console.error("Error in callback for global event ".concat(colors.green(event), "..."), error);
          });
        }));
      }
    })();
  }

  /**
   * Apply filters to event data by calling all subscribed callbacks sequentially, supporting asynchronous operations.
   *
   * @param {string|array|null|undefined} event - The event name or array of event names to apply filters to.
   * @param {any} valueToFilter - The data to filter.
   * @param {object} [additionalData={}] - Additional data to pass to the callback functions.
   * @param {object} [options={}] - Additional options, including `context` to filter context-specific callbacks.
   * @returns {Promise<any>} A promise that resolves to the filtered data after all callbacks have been applied.
   */
  static applyFilters(event, valueToFilter) {
    var _arguments2 = arguments,
      _this2 = this;
    return _asyncToGenerator(function* () {
      var additionalData = _arguments2.length > 2 && _arguments2[2] !== undefined ? _arguments2[2] : {};
      var options = _arguments2.length > 3 && _arguments2[3] !== undefined ? _arguments2[3] : {};
      if (!event) return valueToFilter; // Return original data if event is null or undefined

      if (Array.isArray(event)) {
        additionalData['_primaryEvent'] = event[0]; // Set the primary event for the first item in the array
        additionalData['_events'] = event; // Store all events in the additional data
        return event.reduce(/*#__PURE__*/function () {
          var _ref3 = _asyncToGenerator(function* (prevPromise, e) {
            var currentData = yield prevPromise;
            return _this2.applyFilters(e, currentData, additionalData, options);
          });
          return function (_x, _x2) {
            return _ref3.apply(this, arguments);
          };
        }(), Promise.resolve(valueToFilter));
      }

      // Add the event key (i.e. the full event name) to the parameters
      // (e.g. in case you're watching an entire namespace, but still want to differentiate child events)
      additionalData['_event'] = event;
      additionalData['_events'] = additionalData['_events'] || [event];
      additionalData['_primaryEvent'] = additionalData['_primaryEvent'] || event;
      additionalData['_options'] = options;

      // Get Callbacks watching for the specific event, or any partial namespace matches
      // e.g. 'namespace:subnamespace:event' would trigger for subscribers of 'namespace:subnamespace:event', 'namespace:subnamespace', and 'namespace'
      var matchingCallbacks = [];
      var eventParts = event.split(':');
      for (var i = eventParts.length; i > 0; i--) {
        var currentEvent = eventParts.slice(0, i).join(':');
        var eventCallbacks = _this2.events.get(currentEvent);
        if (eventCallbacks) {
          matchingCallbacks.push(...eventCallbacks);
        }
      }

      // Reduce to only callbacks with the correct context (or global)
      var globalCallbacks = matchingCallbacks.filter(listener => !listener.options.context);
      var contextCallbacks = matchingCallbacks.filter(listener => options.context && listener.options.context === options.context);
      var callbacks = [...contextCallbacks, ...globalCallbacks];

      // Sort the callbacks by priorty
      callbacks.sort((a, b) => a.options.priority - b.options.priority);
      if (!callbacks.length) {
        Debug.log("\uD83C\uDF00 ".concat(colors.blue('Applying filters for event'), ": ").concat(colors.green(event), " but nothing was listening"), 4, {
          valueToFilter,
          additionalData,
          callbacks,
          globalCallbacks,
          contextCallbacks
        });
        return valueToFilter;
      }
      Debug.log("\uD83C\uDF00 ".concat(colors.blue('Applying filters for event'), ": ").concat(colors.green(event), " with ").concat(colors.cyan(callbacks.length), " callbacks"), 2, {
        valueToFilter,
        additionalData,
        callbacks,
        globalCallbacks,
        contextCallbacks
      });
      var currentData = valueToFilter;
      for (var {
        callback,
        context
      } of callbacks) {
        try {
          var newData = yield callback.call(context, currentData, additionalData);
          if (newData !== undefined) {
            currentData = newData;
            if (JSON.stringify(newData) !== JSON.stringify(valueToFilter)) {
              Debug.details("\u270F\uFE0F Data was mutated by callback in event: ".concat(colors.green(event)), {
                originalData: valueToFilter,
                mutatedData: currentData,
                callback
              });
            }
          }
        } catch (error) {
          console.error("Error in filter: ".concat(colors.green(event), "..."), error);
        }
      }
      return currentData;
    })();
  }

  /**
   * A helper to simulate a synchronous-like behavior by ensuring the promises are resolved before continuing
   * Note: this still uses async/await, but should safely maintain the order of operations
   *
   * @param {string|array|null|undefined} event - The event name or array of event names to apply filters to.
   * @param {any} valueToFilter - The data to filter.
   * @param {object} [additionalData={}] - Additional data to pass to the callback functions.
   * @returns {any} The filtered data after all promises have been resolved.
   */
  static applyFiltersSync(event, valueToFilter) {
    var additionalData = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    if (!event) return valueToFilter; // Return original data if event is null or undefined

    if (Array.isArray(event)) {
      additionalData['_primaryEvent'] = event[0]; // Set the primary event for the first item in the array
      additionalData['_events'] = event; // Store all events in the additional data
      return event.reduce((currentData, e) => {
        return this.applyFiltersSync(e, currentData, additionalData);
      }, valueToFilter);
    }

    // Add the event key (i.e. the full event name) to the parameters
    // (e.g. in case you're watching an entire namespace, but still want to differentiate child events)
    additionalData['_event'] = event;
    additionalData['_events'] = additionalData['_events'] || [event];
    additionalData['_primaryEvent'] = additionalData['_primaryEvent'] || event;
    additionalData['_options'] = options;

    // Get all relevant callbacks (only full matches, no partial namespace matches)
    var callbacks = [...(this.events.get(event) || [])].filter(_ref4 => {
      var {
        context
      } = _ref4;
      return !options.context || context === options.context;
    });
    if (!callbacks.length) {
      Debug.log("\uD83C\uDF00 ".concat(colors.blue('Applying filters for event'), ": ").concat(colors.green(event), " but nothing was listening"), 4, {
        valueToFilter,
        additionalData,
        callbacks
      });
      return valueToFilter;
    }
    Debug.log("\uD83C\uDF00 ".concat(colors.blue('Applying filters for event'), ": ").concat(colors.green(event), " with ").concat(colors.cyan(callbacks.length), " callbacks"), 2, {
      valueToFilter,
      additionalData
    });
    var currentData = valueToFilter;
    for (var {
      callback,
      context
    } of callbacks) {
      try {
        var newData = callback.apply(context, [currentData, additionalData]); // Call synchronously

        if (newData instanceof Promise) {
          console.error("applyFiltersSync received an unsupported Promise from callback in event ".concat(colors.green(event), ":"), callback);
          throw new Error("Async functions are not supported in ".concat(colors.yellow('applyFiltersSync'), ". Use ").concat(colors.cyan('applyFilters()'), " instead."));
        }
        if (newData !== undefined) {
          // Only mutate the data if a return value was provided
          currentData = newData;
          if (JSON.stringify(newData) !== JSON.stringify(valueToFilter)) {
            Debug.details("\u270F\uFE0F Data was mutated by callback in event: ".concat(colors.green(event)), {
              originalData: valueToFilter,
              mutatedData: currentData,
              callback
            });
          }
        }
      } catch (error) {
        console.error("Error in filter: ".concat(colors.green(event), "..."), error);
      }
    }
    return currentData;
  }

  /**
   * Observe DOM Mutations and automatically trigger events when they occur
   *
   * @param {*} targetNode
   * @param {*} event
   * @param {*} options
   * @returns
   */
  static observeMutations(targetNode, event) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
      attributes: true,
      childList: true,
      subtree: true
    };
    if (!targetNode) throw new Error("Target node is required for ".concat(colors.yellow('MutationObserver'), "."));
    var observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        this.trigger("DOM:".concat(colors.green(event), ":").concat(mutation.type), {
          targetNode,
          mutation
        }, options);
      });
    });
    observer.observe(targetNode, options);

    // Store the observer in the Map
    this.observers.set(targetNode, observer);

    // Debug logging
    Debug.log("\uD83D\uDC40 Observing mutations on ".concat(colors.yellow(targetNode), " for event: ").concat(colors.green(event)), 3);
    return observer; // Return the observer instance for manual control (e.g., disconnecting later)
  }

  /**
   * Stop observing mutations for a specific target node
   *
   * @param {*} targetNode - The node for which to stop observing mutations
   */
  static stopObservingMutations(targetNode) {
    var observer = this.observers.get(targetNode);
    if (observer) {
      observer.disconnect(); // Stop observing mutations
      this.observers.delete(targetNode); // Remove from the Map

      // Debug logging
      Debug.log("\uD83D\uDEAB Stopped observing mutations on ".concat(colors.yellow(targetNode)), 3);
    } else {
      console.error("No observer found for ".concat(colors.yellow(targetNode)));
    }
  }

  /**
   * Stop all observers and clear the map
   */
  static stopAllObservingMutations() {
    this.observers.forEach((observer, targetNode) => {
      observer.disconnect(); // Stop each observer
      Debug.log("\uD83D\uDEAB Stopped observing mutations on ".concat(colors.yellow(targetNode)), 3);
    });
    this.observers.clear(); // Clear the Map
  }

  /**
   * Watch A DOM element for changes and trigger an event when it changes
   */
  static watchDOM(selector, callback) {
    var _context$dataset;
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    var {
      context = document.body,
      watchAttributes = false,
      watchInputChanges = false,
      pollInterval = 200
    } = options;
    var config = {
      childList: true,
      subtree: true,
      attributes: watchAttributes,
      characterData: watchAttributes
    };
    var currentElements = new Map(); // Map<Element, { listener, poll }>
    var knownValues = new WeakMap(); // Element -> last known value

    var isTextInput = el => {
      var _el$matches;
      return (_el$matches = el.matches) === null || _el$matches === void 0 ? void 0 : _el$matches.call(el, 'input, select, textarea');
    };
    var maybeFireChanged = el => {
      var isInput = isTextInput(el);
      var currentValue = isInput ? el.value : el.innerHTML;
      var prevValue = knownValues.get(el);
      if (prevValue !== undefined && currentValue !== prevValue) {
        knownValues.set(el, currentValue);
        callback(el, 'changed');
      } else if (!knownValues.has(el)) {
        knownValues.set(el, currentValue);
      }
    };
    var bindInputListener = el => {
      if (!watchInputChanges || currentElements.has(el)) return;
      var inputCallback = e => callback(el, 'input-change', e);
      el.addEventListener('input', inputCallback);
      el.addEventListener('change', inputCallback);
      var poller = setInterval(() => {
        if (!document.contains(el)) return;
        maybeFireChanged(el);
      }, pollInterval);
      currentElements.set(el, {
        listener: inputCallback,
        poll: poller
      });
    };
    var unbindInputListener = el => {
      var entry = currentElements.get(el);
      if (entry) {
        el.removeEventListener('input', entry.listener);
        el.removeEventListener('change', entry.listener);
        clearInterval(entry.poll);
        currentElements.delete(el);
      }
      knownValues.delete(el);
    };
    var checkElements = () => {
      var seen = new Set();
      var elements = context.querySelectorAll(selector);
      elements.forEach(el => {
        seen.add(el);
        var isInput = isTextInput(el);
        var currentValue = isInput ? el.value : el.innerHTML;
        if (!currentElements.has(el)) {
          callback(el, 'added');
          knownValues.set(el, currentValue);
          if (watchInputChanges && isInput) bindInputListener(el);
        } else {
          maybeFireChanged(el);
        }
      });
      for (var el of currentElements.keys()) {
        if (!seen.has(el) || !context.contains(el)) {
          callback(null, 'removed');
          unbindInputListener(el);
        }
      }
    };

    // Initial check
    checkElements();
    var observer = new MutationObserver(() => {
      checkElements();
    });
    observer.observe(context, config);
    var observerKey = "".concat(selector, "::").concat(((_context$dataset = context.dataset) === null || _context$dataset === void 0 ? void 0 : _context$dataset.id) || context.className || context.tagName || 'context');
    this.observers.set(observerKey, observer);
    Debug.log("\uD83D\uDC41\uFE0F Watching DOM for ".concat(colors.green(selector), " within ").concat(colors.yellow(context.tagName || 'context'), " (inputChanges: ").concat(watchInputChanges, ")"), 2);
  }

  /**
   * Stop watching a specific DOM element
   * @param {*} selector
   */
  static unwatchDOM(selector) {
    var _context$dataset2;
    var context = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document.body;
    var observerKey = "".concat(selector, "::").concat(((_context$dataset2 = context.dataset) === null || _context$dataset2 === void 0 ? void 0 : _context$dataset2.id) || context.className || context.tagName || 'context');
    var observer = this.observers.get(observerKey);
    if (observer) {
      observer.disconnect();
      this.observers.delete(observerKey);
      Debug.log("\uD83D\uDED1 Unwatched DOM for ".concat(colors.green(selector), " in ").concat(colors.yellow(context.tagName || 'context')), 2);
    } else {
      console.warn("No observer found for ".concat(colors.green(selector), " in ").concat(colors.yellow(context.tagName || 'context')));
    }
  }
}

// Webpack multi-entry (`app` + `template.*`) duplicates sync modules when
// `splitChunks.chunks` is `'async'` only. This class keeps listeners on static
// Maps — without a shared reference, `just-validate` (app) and product form
// (template) would use different buses and ATC/validation would never connect.
_defineProperty(iamotaEventHandler, "events", new Map());
// Reference to event listeners
_defineProperty(iamotaEventHandler, "atomicEvents", new Map());
// Reference to any "one-time" events that had already fired (i.e. reply them when a new listener is added)
_defineProperty(iamotaEventHandler, "observers", new Map());
var __iamotaEhGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : {};
var __IAMOTA_EH_CLASS_KEY__ = '__iamotaEventHandlerClass__';
var IamotaEventHandlerExport = __iamotaEhGlobal[__IAMOTA_EH_CLASS_KEY__] || (__iamotaEhGlobal[__IAMOTA_EH_CLASS_KEY__] = iamotaEventHandler);
export default IamotaEventHandlerExport;